/**
 * React hook for a work item's comment thread.
 *
 * Fetches the initial comment list on mount, subscribes to WebSocket
 * `work_item_comment.{created,updated,deleted}` events, and exposes
 * send/edit/remove actions with optimistic UI updates.
 *
 * The hook accepts an optional `bus` that mirrors `{ subscribe }` semantics
 * so tests can deliver synthetic events without standing up a real
 * `useWebSocket` connection. In production the bus is omitted and the hook
 * falls back to the shared `useWebSocket` plumbing.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { WorkItemCommentDto, WsServerMessage } from "@yagni/shared";
import { useWebSocket } from "./useWebSocket.js";

export interface WorkItemCommentsBus {
  /**
   * Subscribe to incoming WS messages. Returns an unsubscribe function that
   * is called on unmount or when `workItemId` changes.
   */
  subscribe: (cb: (msg: unknown) => void) => () => void;
}

export interface UseWorkItemCommentsOptions {
  bus?: WorkItemCommentsBus;
}

export interface UseWorkItemCommentsResult {
  comments: WorkItemCommentDto[];
  loading: boolean;
  error: string | null;
  send: (body: string) => Promise<void>;
  edit: (commentId: string, body: string) => Promise<void>;
  remove: (commentId: string) => Promise<void>;
  reload: () => Promise<void>;
}

interface WorkItemCommentCreatedEvent {
  type: "work_item_comment.created";
  workItemId: string;
  comment: WorkItemCommentDto;
}

interface WorkItemCommentUpdatedEvent {
  type: "work_item_comment.updated";
  workItemId: string;
  commentId: string;
  body: string;
  editedAt: string;
}

interface WorkItemCommentDeletedEvent {
  type: "work_item_comment.deleted";
  workItemId: string;
  commentId: string;
}

type WorkItemCommentEvent =
  | WorkItemCommentCreatedEvent
  | WorkItemCommentUpdatedEvent
  | WorkItemCommentDeletedEvent;

function isCommentEvent(msg: unknown): msg is WorkItemCommentEvent {
  if (!msg || typeof msg !== "object") return false;
  const type = (msg as { type?: unknown }).type;
  return (
    type === "work_item_comment.created" ||
    type === "work_item_comment.updated" ||
    type === "work_item_comment.deleted"
  );
}

/**
 * Insert or update a comment in the list, deduping by id. Preserves
 * chronological (createdAt ascending) order so late-arriving events never
 * shuffle the thread.
 */
function upsertComment(
  prev: WorkItemCommentDto[],
  next: WorkItemCommentDto,
): WorkItemCommentDto[] {
  const existingIdx = prev.findIndex((c) => c.id === next.id);
  if (existingIdx !== -1) {
    const copy = [...prev];
    copy[existingIdx] = next;
    return copy;
  }
  // Insert in createdAt order. Most of the time `next` is newest, so a tail
  // append is the common path.
  const inserted = [...prev, next];
  inserted.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  return inserted;
}

export function useWorkItemComments(
  workItemId: string,
  options: UseWorkItemCommentsOptions = {},
): UseWorkItemCommentsResult {
  const [comments, setComments] = useState<WorkItemCommentDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Stable ref onto the latest workItemId so WS callbacks always filter on
  // the hook's current target rather than the workItemId captured when the
  // bus subscription was registered.
  const workItemIdRef = useRef(workItemId);
  workItemIdRef.current = workItemId;

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `/api/work-items/${encodeURIComponent(workItemId)}/comments`,
        { credentials: "include" },
      );
      if (!res.ok) {
        setComments([]);
        setError(`Failed to load comments (HTTP ${res.status})`);
        return;
      }
      const data = (await res.json()) as { comments: WorkItemCommentDto[] };
      setComments(data.comments ?? []);
    } catch (err) {
      setComments([]);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [workItemId]);

  // Initial fetch + re-fetch on workItemId change.
  useEffect(() => {
    void reload();
  }, [reload]);

  // WS handler: apply events that match the current workItemId only.
  const handleEvent = useCallback((msg: unknown) => {
    if (!isCommentEvent(msg)) return;
    if (msg.workItemId !== workItemIdRef.current) return;

    if (msg.type === "work_item_comment.created") {
      setComments((prev) => upsertComment(prev, msg.comment));
      return;
    }
    if (msg.type === "work_item_comment.updated") {
      setComments((prev) =>
        prev.map((c) =>
          c.id === msg.commentId
            ? { ...c, body: msg.body, editedAt: msg.editedAt }
            : c,
        ),
      );
      return;
    }
    if (msg.type === "work_item_comment.deleted") {
      setComments((prev) => prev.filter((c) => c.id !== msg.commentId));
      return;
    }
  }, []);

  // Bus path: prefer the injected test bus, fall back to the real
  // `useWebSocket` connection. Both branches are unconditional hook calls —
  // we can't guard `useWebSocket` behind `options.bus` (hooks rules) — so the
  // fallback always registers but its `onMessage` is a no-op when a bus is
  // provided.
  useEffect(() => {
    const bus = options.bus;
    if (!bus) return;
    const unsubscribe = bus.subscribe((msg) => handleEvent(msg));
    return () => {
      unsubscribe();
    };
  }, [options.bus, handleEvent]);

  const wsOnMessage = useCallback(
    (msg: WsServerMessage) => {
      if (options.bus) return; // Test bus is authoritative when present.
      handleEvent(msg);
    },
    [options.bus, handleEvent],
  );
  useWebSocket(wsOnMessage);

  const send = useCallback(
    async (body: string) => {
      const trimmed = body.trim();
      if (!trimmed) return;
      setError(null);

      // Optimistic placeholder so the UI feels instant. The placeholder id
      // is prefixed with `optimistic-` so it can never collide with a real
      // server-assigned id (UUIDs from pg are never prefixed this way).
      const optimisticId = `optimistic-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const optimistic: WorkItemCommentDto = {
        id: optimisticId,
        workItemId,
        authorId: "",
        authorType: "human",
        authorName: null,
        body: trimmed,
        createdAt: new Date().toISOString(),
        editedAt: null,
      };
      setComments((prev) => [...prev, optimistic]);

      try {
        const res = await fetch(
          `/api/work-items/${encodeURIComponent(workItemId)}/comments`,
          {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ body: trimmed }),
          },
        );
        if (!res.ok) {
          setComments((prev) => prev.filter((c) => c.id !== optimisticId));
          setError(`Failed to send comment (HTTP ${res.status})`);
          return;
        }
        const data = (await res.json()) as { comment: WorkItemCommentDto };
        // Reconcile: drop the optimistic placeholder and upsert the real
        // comment. If the WS echo already delivered the real id, `upsertComment`
        // replaces in place rather than duplicating.
        setComments((prev) => {
          const withoutOptimistic = prev.filter((c) => c.id !== optimisticId);
          return upsertComment(withoutOptimistic, data.comment);
        });
      } catch (err) {
        setComments((prev) => prev.filter((c) => c.id !== optimisticId));
        setError(err instanceof Error ? err.message : String(err));
      }
    },
    [workItemId],
  );

  const edit = useCallback(
    async (commentId: string, body: string) => {
      const trimmed = body.trim();
      if (!trimmed) return;
      setError(null);
      try {
        const res = await fetch(
          `/api/work-items/${encodeURIComponent(workItemId)}/comments/${encodeURIComponent(commentId)}`,
          {
            method: "PATCH",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ body: trimmed }),
          },
        );
        if (!res.ok) {
          setError(`Failed to edit comment (HTTP ${res.status})`);
          return;
        }
        // Apply the response immediately so edits made while the WS echo is
        // in flight don't visually "flicker". The WS echo will re-apply the
        // same values — `upsertComment` makes that idempotent.
        const data = (await res.json()) as { comment: WorkItemCommentDto };
        setComments((prev) => upsertComment(prev, data.comment));
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    },
    [workItemId],
  );

  const remove = useCallback(
    async (commentId: string) => {
      setError(null);
      try {
        const res = await fetch(
          `/api/work-items/${encodeURIComponent(workItemId)}/comments/${encodeURIComponent(commentId)}`,
          { method: "DELETE", credentials: "include" },
        );
        if (!res.ok && res.status !== 204) {
          setError(`Failed to delete comment (HTTP ${res.status})`);
          return;
        }
        // Apply deletion locally rather than waiting for the WS echo so the
        // UI responds to the click without a round-trip delay. The echo
        // will no-op because the id is already gone.
        setComments((prev) => prev.filter((c) => c.id !== commentId));
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    },
    [workItemId],
  );

  return { comments, loading, error, send, edit, remove, reload };
}
